/**
 * ChatTaskAckCard — initial acknowledgment card emitted on the chat thread
 * the moment a FULL_AUTO task is enqueued. Carries a deep link into Mission
 * Control filtered to the `runId` so the user can pop the drawer on click.
 *
 * Phase 3 - Task 14.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { AGENTS, type AgentId } from '@/tokens/colors';

export interface ChatTaskAckCardProps {
  readonly runId: string;
  readonly title: string;
  readonly agent: AgentId;
  readonly packageName: string;
}

export function ChatTaskAckCard({
  runId,
  title,
  agent,
  packageName,
}: ChatTaskAckCardProps): ReactElement {
  const identity = AGENTS[agent] ?? AGENTS.developer;
  const stripe = identity.light;

  return (
    <article
      data-agent={agent}
      data-runid={runId}
      className="flex items-stretch gap-3 rounded-lg border border-zinc-700/50 bg-zinc-900/40 p-3"
    >
      <span
        aria-hidden="true"
        className="w-1 rounded"
        style={{ background: stripe }}
      />
      <div className="flex-1 min-w-0">
        <header className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-zinc-400">
            Full-Auto enqueued
          </span>
          <span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">
            {packageName}
          </span>
        </header>
        <h3 className="mt-1 truncate font-medium text-zinc-100">{title}</h3>
        <Link
          to={`/tasks?runId=${runId}`}
          className="mt-2 inline-block text-sm text-sky-400 hover:underline"
        >
          View in Mission Control →
        </Link>
      </div>
    </article>
  );
}

export default ChatTaskAckCard;
